import React, { useCallback, useEffect, useMemo } from "react";
import * as Scrivito from "scrivito";
import { camelCase } from "lodash-es";
import { handleProjectListFiltersUpdate, updateProjectFilters } from "utils";
import cx from "classnames";
import { useTranslate } from "hooks";
import type { IconName } from "components/Icon/iconNames";
import type { Filter, ProjectStatus } from "interfaces/project/project.types";
import { FilterBtn } from "./FilterBtn";
import { useProjectListContext } from "../../hooks/useProjectListContext";

type ProjectListFiltersProps = {
  className?: string;
};

type OverallStatistics = {
  notShared?: number;
  shared?: number;
  statuses?: Record<string, number>;
} | null;

type StatisticsRecord = Record<string, number | undefined> | null;

export const ProjectListFilters = Scrivito.connect(
  ({ className }: ProjectListFiltersProps) => {
    const translate = useTranslate();
    const {
      overallStatistics: rawOverallStatistics,
      projectsCount,
      setSearchQuery,
      setSharedStatusSwitcher,
      projectStatuses: rawProjectStatuses,
      setFilterStatuses,
      handleSetCurrentPage,
      filters: rawFilters,
      setFilters: rawSetFilters,
      statistics: rawStatistics,
    } = useProjectListContext();

    const filters = rawFilters as Filter[];
    const setFilters = rawSetFilters as React.Dispatch<
      React.SetStateAction<Filter[]>
    >;
    const overallStatistics = rawOverallStatistics as OverallStatistics;
    const projectStatuses = rawProjectStatuses as ProjectStatus[];
    const statistics = rawStatistics as StatisticsRecord;

    const translations = useMemo(
      () => ({
        shared: translate.stringOnly("SHARED"),
        notShared: translate.stringOnly("NOT_SHARED"),
        allProjects: translate.stringOnly("ALL_PROJECTS"),
      }),
      [translate],
    );

    useEffect(() => {
      if (filters.length > 1) return;
      if (!overallStatistics || !Object.keys(overallStatistics).length) return;

      const newFilters = updateProjectFilters(
        translations,
        overallStatistics,
        projectsCount,
        setSearchQuery,
        setSharedStatusSwitcher as (
          callback: (prev: boolean | null) => boolean | null,
        ) => void,
        setFilters,
        projectStatuses,
      );
      setFilters(newFilters);
    }, [
      filters.length,
      translations,
      overallStatistics,
      projectsCount,
      setSearchQuery,
      setSharedStatusSwitcher,
      setFilters,
      projectStatuses,
    ]);

    useEffect(() => {
      setFilterStatuses(
        filters?.reduce<string[]>((newStatuses, filter) => {
          if (filter.isStatusFilter && filter.isActive) {
            newStatuses.push(filter.name);
          }
          return newStatuses;
        }, []),
      );
    }, [filters, setFilterStatuses]);

    useEffect(() => {
      if (!statistics && !overallStatistics) {
        return;
      }

      setFilters((prevFilters) =>
        prevFilters?.map((fl) => {
          let count = 0;
          const flCamelCase = camelCase(fl.id);

          if (flCamelCase === "allProjects" && projectsCount) {
            count = projectsCount;
          }
          if (flCamelCase === "shared" || flCamelCase === "notShared") {
            count = overallStatistics?.[flCamelCase] ?? 0;
          }

          if (statistics?.[flCamelCase]) {
            count = statistics[flCamelCase] || 0;
          } else if (overallStatistics?.statuses?.[fl.id]) {
            count = overallStatistics?.statuses[fl.id];
          }

          return {
            ...fl,
            count,
          };
        }),
      );
    }, [statistics, overallStatistics, projectsCount, setFilters]);

    const handleFilterActivation = useCallback(
      (id: string) => {
        setFilters((prevFilters) =>
          handleProjectListFiltersUpdate(prevFilters, { id }),
        );
        handleSetCurrentPage();
      },
      [setFilters, handleSetCurrentPage],
    );

    return (
      <div className={cx("d-flex flex-wrap gap-2", className)}>
        {filters.map(
          ({ id, name, count, color, icon, isActive, disabled, onClick }) => (
            <FilterBtn
              key={id}
              id={id}
              name={name}
              count={count ?? 0}
              color={color}
              icon={icon as IconName}
              isActive={isActive}
              onClick={(value) => {
                if (onClick) {
                  onClick();
                  return;
                }

                handleFilterActivation(value);
              }}
              disabled={disabled}
            />
          ),
        )}
      </div>
    );
  },
);
